import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import { Minimize } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { useAppState } from 'src/state-management/state-provider';
import { FileDetail } from 'src/api/Api';
import { AxiosError } from 'axios';
import clientService from 'src/clientService';
import { getEntityUrnById } from 'src/util/entity-util';
import AudioPlayer from '../components/AudioPlayer';
import InplaceImage from '../components/InplaceImage';

interface DataPanelProps {
  isOpen: boolean;
  isMaximized: boolean;
  onClose: () => void;
  onMaximize: () => void;
}

const DataPanel: React.FC<DataPanelProps> = ({
  isOpen,
  isMaximized,
  onClose,
  onMaximize,
}) => {
  const { state } = useAppState();
  const [files, setFiles] = useState<FileDetail[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const rowsPerPage = 9;

  const getEntityId = () => {
    const {
      bioInteractiveMapFilter,
      selectedProject: { projectDetails },
    } = state;
    if (bioInteractiveMapFilter.selectedDeployment) {
      return getEntityUrnById(
        'deployment',
        bioInteractiveMapFilter.selectedDeployment.deployment_id,
      );
    }
    if (bioInteractiveMapFilter.selectedLocation) {
      return getEntityUrnById(
        'location',
        bioInteractiveMapFilter.selectedLocation.location_id,
      );
    }
    if (bioInteractiveMapFilter.selectedSite) {
      return getEntityUrnById(
        'site',
        bioInteractiveMapFilter.selectedSite.site_id,
      );
    }
    if (projectDetails) {
      return getEntityUrnById('project', projectDetails.project_id);
    }
    return undefined;
  };

  const getEntityName = () => {
    const {
      bioInteractiveMapFilter,
      selectedProject: { projectDetails, bioMapData },
    } = state;

    if (
      bioInteractiveMapFilter.selectedDeployment?.deployment_id &&
      bioMapData?.deployments
    ) {
      const deployment = (bioMapData.deployments as any[]).find(
        (d) =>
          d.deployment_id ===
          bioInteractiveMapFilter.selectedDeployment?.deployment_id,
      );
      return deployment?.name;
    }

    if (
      bioInteractiveMapFilter.selectedLocation?.location_id &&
      bioMapData?.locations
    ) {
      const location = (bioMapData.locations as any[]).find(
        (l) =>
          l.location_id ===
          bioInteractiveMapFilter.selectedLocation?.location_id,
      );
      return location?.location_name;
    }

    if (bioInteractiveMapFilter.selectedSite?.site_id && bioMapData?.sites) {
      const site = (bioMapData.sites as any[]).find(
        (s) => s.site_id === bioInteractiveMapFilter.selectedSite?.site_id,
      );
      return site?.site_name;
    }

    if (projectDetails) {
      return projectDetails.project_name;
    }
    return null;
  };

  const fetchFiles = async (skip: number) => {
    setLoading(true);
    const entityId = getEntityId();
    if (!entityId) {
      setLoading(false);
      setFiles([]);
      setTotal(0);
      return;
    }

    try {
      const response =
        await clientService.api.readFilesWithInferencesApiV1InferencesFilesGet(
          {
            entityId,
            skip,
            limit: rowsPerPage,
          },
        );
      setFiles(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      const e = error as AxiosError;
      console.error('Error fetching files:', e.response?.data);
      setFiles([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchFiles(0);
      setPage(0);
    }
  }, [isOpen, state.bioInteractiveMapFilter]);

  const handleNextPage = () => {
    const newPage = page + 1;
    fetchFiles(newPage * rowsPerPage);
    setPage(newPage);
  };

  const handlePrevPage = () => {
    const newPage = page - 1;
    fetchFiles(newPage * rowsPerPage);
    setPage(newPage);
  };

  const entityName = getEntityName();

  return (
    <Drawer
      anchor="bottom"
      open={isOpen}
      onClose={onClose}
      variant="temporary"
      ModalProps={{
        hideBackdrop: true,
      }}
      PaperProps={{
        sx: {
          zIndex: 999,
          height: isMaximized ? '100%' : '50%',
          maxHeight: '90vh',
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          transition: (theme) =>
            theme.transitions.create('height', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      <Box
        sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">
            {entityName ? `${entityName} > ` : ''}Files{' '}
            {total > 0 && `(${total})`}
          </Typography>
          <Box>
            <IconButton onClick={onMaximize} aria-label="maximize">
              {isMaximized ? <Minimize /> : <FullscreenIcon />}
            </IconButton>
            <IconButton onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          ) : files.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No files found.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {files.map((file) => {
                const tooltipTitle = [
                  file.site_name ? `Site: ${file.site_name}` : undefined,
                  file.location_name ? `Location: ${file.location_name}` : undefined,
                ]
                  .filter(Boolean)
                  .join(' | ');

                return (
                  <Grid item xs={12} sm={6} md={4} key={file.file.file_id}>
                    <Card>
                      <CardMedia>
                        <InplaceImage
                          alt={file.file.name}
                          fileId={file.file.file_id}
                        />
                      </CardMedia>
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography
                            gutterBottom
                            variant="h6"
                            component="div"
                            title={file.file.name}
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {file.file.name}
                          </Typography>
                          {tooltipTitle && (
                            <Tooltip title={tooltipTitle}>
                              <IconButton size="small">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <AudioPlayer fileId={file.file.file_id} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={handlePrevPage} disabled={page === 0 || loading}>
            Previous
          </Button>
          <Button
            onClick={handleNextPage}
            disabled={page * rowsPerPage + rowsPerPage >= total || loading}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DataPanel;
